import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Type, Copy, Check } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";
import { useToast } from "@/hooks/use-toast";

const loremBase = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.";

const LoremIpsumGenerator = () => {
  const { toast } = useToast();
  const [paragraphs, setParagraphs] = useState<string>("3");
  const [copied, setCopied] = useState(false);
  const [generated, setGenerated] = useState<string>("");

  const generate = () => {
    const count = Math.min(Math.max(parseInt(paragraphs) || 1, 1), 10);
    const result = Array(count).fill(loremBase).join("\n\n");
    setGenerated(result);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(generated);
    setCopied(true);
    toast({ title: "Copied!" });
    setTimeout(() => setCopied(false), 2000);
  };

  const relatedTools = getRelatedTools("lorem-ipsum");

  return (
    <ToolPageLayout
      title="Lorem Ipsum Generator"
      description="Generate placeholder text for your designs and mockups."
      metaDescription="Generate Lorem Ipsum placeholder text for designs, mockups, and prototypes. Customize the number of paragraphs for your layout needs."
      icon={Type}
      slug="lorem-ipsum"
      formula={{ equation: "N/A - Text generation tool", variables: [] }}
      example={{ inputs: [{ label: "Paragraphs", value: "3" }], output: "3 paragraphs of Lorem Ipsum", interpretation: "Use placeholder text to visualize layouts before final content is ready." }}
      improvements={["Use realistic word counts for accurate layouts", "Replace placeholder text before publishing", "Consider using real content samples when possible"]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
    >
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Generate Lorem Ipsum</CardTitle>
          <CardDescription>Choose number of paragraphs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="paragraphs">Paragraphs (1-10)</Label>
              <Input id="paragraphs" type="number" min="1" max="10" value={paragraphs} onChange={(e) => setParagraphs(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={generate}>Generate</Button>
            </div>
          </div>
          {generated && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg max-h-64 overflow-y-auto text-sm text-muted-foreground whitespace-pre-wrap">{generated}</div>
              <Button onClick={copy} variant="outline" className="w-full">
                {copied ? <><Check className="w-4 h-4 mr-2" />Copied!</> : <><Copy className="w-4 h-4 mr-2" />Copy Text</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default LoremIpsumGenerator;
